<script setup lang="ts">
/**
 * One icon, painted.
 *
 * Every icon component in the app renders through here so that a change to the stroke rule, the
 * dot size or the dash reaches all of them at once. The drawings live in `src/lib/icons.ts`.
 */
import { computed } from 'vue';

import { ICONS, dotRadius, strokeFor } from '../lib/icons';

const props = defineProps<{ name: string; size?: number }>();

const px = computed(() => props.size ?? 20);
const glyph = computed(() => ICONS[props.name] ?? {});
const stroke = computed(() => strokeFor(px.value));
const radius = computed(() => dotRadius(stroke.value, glyph.value.dotScale ?? 1));
/** In user units, so a dash is the same length on the screen whatever size the icon is drawn at. */
const dashes = computed(() => `${stroke.value * 2.4} ${stroke.value * 1.9}`);
</script>

<template>
  <svg
    :width="px" :height="px" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" :stroke-width="stroke" stroke-linecap="round" stroke-linejoin="round"
    aria-hidden="true" focusable="false"
  >
    <path v-for="(d, i) in (glyph.d ?? [])" :key="`s${i}`" :d="d" />
    <path
      v-for="(d, i) in (glyph.dash ?? [])" :key="`h${i}`" :d="d" :stroke-dasharray="dashes"
    />
    <path v-for="(d, i) in (glyph.fill ?? [])" :key="`f${i}`" :d="d" fill="currentColor" stroke="none" />
    <rect
      v-for="(bar, i) in (glyph.bars ?? [])" :key="`b${i}`"
      :x="bar[0]" :y="bar[1]" :width="bar[2]" :height="bar[3]"
      :rx="Math.min(bar[2], bar[3]) / 2" fill="currentColor" stroke="none"
    />
    <circle
      v-for="(dot, i) in (glyph.dots ?? [])" :key="`d${i}`"
      :cx="dot[0]" :cy="dot[1]" :r="radius" fill="currentColor" stroke="none"
    />
  </svg>
</template>
